import { useCallback } from 'react';
import { useEnabledFeatures } from '../hooks/enabled-features/use-enabled-features';
import { useToast, Button } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useApplicationType } from '../hooks/use-application-type';
import { useGenerateReportingEventNumber } from '../hooks/use-generate-reporting-event-number';
import { FormButton } from '../../forms/integrated-form-components/form-button';
import { useEventMetadataStore } from '../../event-panel/event-panel-form/event-metadata';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';
import { useCurrentUser } from '../../current-user/current-user';

export const GenerateRenButton = observer(function GenerateRenButton({
    eventId,
}: {
    eventId?: number;
}) {
    const bootstrapState = useBootstrapState();
    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();
    const currentUser = useCurrentUser();
    const toast = useToast();
    const renLabel =
        bootstrapState.fieldDisplayNameByFieldName['AGENCY_EVENT_REPORTING_EVENT_NUMBER'];
    const [generateReportingEventNumber, { isLoading }] = useGenerateReportingEventNumber();
    const eventMetadata = useEventMetadataStore().state.metadata;
    const agencyEventNumber = eventMetadata?.agencyEventNumber;
    const agencyId = eventMetadata?.agencyId;
    const showGenerateRenPrimaryAgencyButton = eventMetadata?.showGenerateRenPrimaryAgencyButton;
    const numberOfRensForPrimaryAgency = agencyId
        ? (eventMetadata?.agencyIdToReportingEventNumbers[agencyId] || []).length
        : 0;
    const { firstResponderCanGenerateRen } = useEnabledFeatures();

    const generateRen = useCallback(() => {
        if (isFirstResponder && !unitAssignment?.unitAgencyId && !currentUser?.departmentAgencyId) {
            toast({
                status: 'error',
                description: `Unable to generate ${renLabel} due to insufficient data`,
            });

            return;
        }

        if (eventId && agencyEventNumber) {
            generateReportingEventNumber({
                agencyEventId: eventId,
                agencyEventNumber,
                numberOfRensForPrimaryAgency,
            });
        }
    }, [
        eventId,
        agencyEventNumber,
        numberOfRensForPrimaryAgency,
        generateReportingEventNumber,
        isFirstResponder,
        unitAssignment,
        currentUser,
        renLabel,
        toast,
    ]);

    if (isFirstResponder) {
        return (
            <Button
                isLoading={isLoading}
                isDisabled={!firstResponderCanGenerateRen || !showGenerateRenPrimaryAgencyButton}
                onClick={generateRen}
            >
                Generate {renLabel}
            </Button>
        );
    }

    if (showGenerateRenPrimaryAgencyButton) {
        return (
            <FormButton variant="ghost" isLoading={isLoading} onClick={generateRen}>
                Generate {renLabel}
            </FormButton>
        );
    }

    return null;
});
